<template>
  <div class="delivery-time">
    <icon-truck />

    <span
      v-if="immediateDelivery"
      class="delivery-time__text text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
      v-text="$string('product.immediate_delivery')"
    />

    <span
      class="delivery-time__text text-mobile-paragraph-s-medium text-desktop-paragraph-s-medium"
      v-text="deliveryTimeText"
    />
  </div>
  <div v-if="isTest" class="delivery-time delivery-time__payment">
    <icon-paypal />
    <span
      class="delivery-time__text text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
      v-text="$string('product.paypal_30days')"
    />

    <button
      ref="ppTrigger"
      type="button"
      class="delivery-time__info icon-button text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
    >
      <icon-info />
      <div class="delivery-time__wrapper">
        <div
          ref="ppProxy"
          class="delivery-time__pp-proxy"
          data-pp-message
          :data-pp-amount="amount"
          data-pp-placement="product"
          :data-pp-currency="isSandbox ? 'USD' : 'EUR'"
          data-pp-style-layout="text"
          data-pp-style-logo-type="none"
          data-page-type="product-details"
        ></div>
      </div>
    </button>
  </div>
  <div v-if="isTest" class="delivery-time delivery-time__klarna">
    <icon-klarna />
    <span
      class="delivery-time__text text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
      v-html="klarnaText"
    />
  </div>
</template>

<script>
/**
 * Vue: Delivery time (delivery-time)
 */
import IconTruck from '~icons/general/truck.svg';
import IconPaypal from '~icons/general/paypal.svg';
import IconKlarna from '~icons/general/klarna.svg';
import IconInfo from '~icons/general/info.svg';
import { loadScript } from '@paypal/paypal-js';

export default {
  name: 'DeliveryTime',

  components: {
    IconTruck,
    IconPaypal,
    IconKlarna,
    IconInfo,
  },

  props: {
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      paypalAnchor: null,
      paypalClickHandler: null,
      klarnaNumber: 1,
    };
  },

  computed: {
    isSandbox() {
      return cnvs.abtest.clientId === 'sb';
    },

    /**
     * Computes the number as it varies per PDP
     */
    klarnaText() {
      return this.$string('product.klarna_30days_html', {
        replace: { number: this.klarnaNumber },
      });
    },

    isTest() {
      const kameleoonExperiment =
        // eslint-disable-next-line
        globalThis.Kameleoon?.API?.Experiments?.getById(329134);
      return Boolean(
        kameleoonExperiment?.associatedVariation?.id === 1161773 &&
          kameleoonExperiment?.active,
      );
    },

    immediateDelivery() {
      return this.$variable('product.immediateDelivery')?.includes(
        this.$variable('product.deliveryTime'),
      );
    },

    amount() {
      return this.product.selectedOrFirstAvailableVariant.price.amount / 100;
    },

    deliveryTimeText() {
      if (this.isGiftCard) {
        return this.$string('product.delivery_time_giftcard');
      }
      return this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
    },
  },

  mounted() {
    if (!this.isTest) return;

    this.addKlarnaText();

    // Load PayPal JS SDK with messages component only for sandbox we use 'sb'
    loadScript({
      'client-id': cnvs.abtest.clientId,
      components: 'messages',
      currency: 'EUR',
    })
      .then((paypal) => {
        this.renderPaypalMessage(paypal);
      })
      .catch((err) => {
        console.error('failed to load the PayPal JS SDK script', err);
      });
  },

  methods: {
    renderPaypalMessage(paypal) {
      const proxy = this.$refs.ppProxy;
      const trigger = this.$refs.ppTrigger;
      if (!proxy || !trigger || !paypal || !paypal.Messages) return;

      paypal
        .Messages({ amount: this.amount })
        .render(proxy)
        .then(() => {
          // Find PayPal’s clickable anchor inside the proxy
          this.paypalAnchor =
            proxy.querySelector('a, [role="link"], [tabindex]') || null;

          // Forward clicks from your info button to the PayPal anchor (opens the official modal)
          this.paypalClickHandler = () => {
            if (this.paypalAnchor) this.paypalAnchor.click();
          };
          trigger.addEventListener('click', this.paypalClickHandler);
        })
        .catch(() => {
          // fail silently
        });
    },

    /**
     * Adds Klarna text to the product sources section.
     * - For A/B Test only
     */
    addKlarnaText() {
      const sourcesWrapper = document.querySelector(
        '.product-sources__content > .metafield-rich_text_field',
      );
      const sources = document.querySelectorAll(
        '.product-sources__content > .metafield-rich_text_field > p',
      );
      const getSupNumber = sources.length + 1;
      this.klarnaNumber = getSupNumber;

      const paragraph = document.createElement('p');
      paragraph.innerHTML = this.$string('product.klarna_30days_text_html', {
        replace: { number: this.klarnaNumber },
      });
      sourcesWrapper.append(paragraph);
    },
  },
};
</script>

<style lang="scss">
@import './delivery-time';
</style>
